// src/pages/AddManagerPage/AddManagerPage.jsx
import React, { useState, useEffect } from "react";
import "./AddManagerPage.css";
import { Plus, Edit, Trash2, X } from "lucide-react";

export default function AddManagerPage() {
  const API_URL = process.env.REACT_APP_API_URL || "";

  // table & modals state
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [message, setMessage] = useState(null); // { type: "success"|"error", text }

  // add-manager modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    email: "",
    position: "",
    salary: "",
    password: "",
  });
  const [adding, setAdding] = useState(false);

  // edit-manager modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingManager, setEditingManager] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // common helper to read auth token
  function getAuthHeaders(json = true) {
    const headers = {};
    if (json) headers["Content-Type"] = "application/json";
    const token = localStorage.getItem("token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  }

  async function fetchManagers() {
    try {
      setLoadingManagers(true);
      const res = await fetch(`${API_URL}/api/users`, {
        headers: getAuthHeaders(false),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setManagers(
        (Array.isArray(data) ? data : []).filter((u) => u.role === "manager")
      );
    } catch (err) {
      console.error("fetchManagers:", err);
      setMessage({
        type: "error",
        text: `Failed to load managers: ${err.message}`,
      });
      setManagers([]);
    } finally {
      setLoadingManagers(false);
    }
  }

  useEffect(() => {
    fetchManagers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------- Add Manager ----------------
  function handleAddChange(e) {
    setAddForm({ ...addForm, [e.target.name]: e.target.value });
  }

  async function handleAddSubmit(e) {
    e && e.preventDefault();
    setMessage(null);

    if (!addForm.name || !addForm.email || !addForm.password) {
      setMessage({
        type: "error",
        text: "Name, email and password are required.",
      });
      return;
    }

    try {
      setAdding(true);
      const res = await fetch(`${API_URL}/api/users`, {
        method: "POST",
        headers: getAuthHeaders(true),
        body: JSON.stringify({
          ...addForm,
          role: "manager",
        }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(
          (data && (data.error || data.message)) || `Status ${res.status}`
        );
      }

      setManagers((prev) => [...prev, data]);
      setMessage({ type: "success", text: "Manager added successfully." });
      setAddForm({
        name: "",
        email: "",
        position: "",
        salary: "",
        password: "",
      });
      setShowAddModal(false);
    } catch (err) {
      console.error("Add manager error:", err);
      setMessage({
        type: "error",
        text: err.message || "Failed to add manager",
      });
    } finally {
      setAdding(false);
    }
  }

  // -------------- Edit Manager ----------------
  function openEditModal(manager) {
    setEditingManager({ ...manager });
    setShowEditModal(true);
    setMessage(null);
  }

  function handleEditChange(e) {
    setEditingManager({ ...editingManager, [e.target.name]: e.target.value });
  }

  async function handleSaveEdit() {
    if (!editingManager) return;
    setMessage(null);

    try {
      setSavingEdit(true);

      const res = await fetch(
        `${API_URL}/api/users/update-by-email/${encodeURIComponent(
          editingManager.email
        )}`,
        {
          method: "PUT",
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            name: editingManager.name,
            position: editingManager.position,
            salary: editingManager.salary,
            role: editingManager.role || "manager",
          }),
        }
      );

      const data = await res.json().catch(() => null);
      if (!res.ok)
        throw new Error(
          (data && (data.error || data.message)) || `Status ${res.status}`
        );

      setManagers((prev) =>
        prev.map((m) => (m.email === data.email ? data : m))
      );
      setMessage({ type: "success", text: "Manager updated." });
      setShowEditModal(false);
      setEditingManager(null);
    } catch (err) {
      console.error("Save edit error:", err);
      setMessage({
        type: "error",
        text: err.message || "Failed to update manager",
      });
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleDeleteManager() {
    if (!editingManager) return;
    if (!window.confirm("Delete this manager? This action cannot be undone."))
      return;

    try {
      setDeleting(true);
      const res = await fetch(`${API_URL}/api/users/${editingManager.id}`, {
        method: "DELETE",
        headers: getAuthHeaders(true),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok)
        throw new Error(
          (data && (data.error || data.message)) || `Status ${res.status}`
        );

      setManagers((prev) => prev.filter((m) => m.id !== editingManager.id));
      setMessage({ type: "success", text: "Manager deleted." });
      setShowEditModal(false);
      setEditingManager(null);
    } catch (err) {
      console.error("Delete manager error:", err);
      setMessage({
        type: "error",
        text: err.message || "Failed to delete manager",
      });
    } finally {
      setDeleting(false);
    }
  }

  // message auto clear
  useEffect(() => {
    if (message) {
      const t = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(t);
    }
  }, [message]);

  return (
    <div className="addmgr-wrapper full-pagee">
      <div className="addmgr-toolbar" style={{ width: "100%", maxWidth: 1200 }}>
        <h2 className="addmgr-title">Managers</h2>

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button
            className="floating-add-btn"
            onClick={() => {
              setShowAddModal(true);
              setMessage(null);
            }}
            title="Add manager"
            aria-label="Add manager"
          >
            <Plus size={16} /> Add Manager
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`addmgr-msg ${
            message.type === "success" ? "success" : "error"
          }`}
        >
          {message.text}
        </div>
      )}

      <div
        className="manager-table-section"
        style={{ width: "100%", maxWidth: 1200 }}
      >
        <h3 style={{ marginTop: 0 }}>All Managers</h3>

        {loadingManagers ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="table-wrap">
            <table className="manager-table" role="table" aria-label="Managers">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Position</th>
                  <th>Salary</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {managers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      style={{ textAlign: "center", padding: 24 }}
                    >
                      No managers found.
                    </td>
                  </tr>
                ) : (
                  managers.map((mgr) => (
                    <tr key={mgr.id}>
                      <td>{mgr.name}</td>
                      <td>{mgr.email}</td>
                      <td>{mgr.position || "-"}</td>
                      <td>{mgr.salary ?? "-"}</td>
                      <td>
                        <button
                          className="icon-btn"
                          onClick={() => openEditModal(mgr)}
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="icon-btn danger"
                          onClick={() => {
                            setEditingManager(mgr);
                            if (window.confirm("Delete this manager?")) {
                              (async () => {
                                try {
                                  const res = await fetch(
                                    `${API_URL}/api/users/${mgr.id}`,
                                    {
                                      method: "DELETE",
                                      headers: getAuthHeaders(true),
                                    }
                                  );
                                  const data = await res
                                    .json()
                                    .catch(() => null);
                                  if (!res.ok)
                                    throw new Error(
                                      (data && (data.error || data.message)) ||
                                        `Status ${res.status}`
                                    );
                                  setManagers((prev) =>
                                    prev.filter((m) => m.id !== mgr.id)
                                  );
                                  setMessage({
                                    type: "success",
                                    text: "Manager deleted.",
                                  });
                                } catch (err) {
                                  console.error("Delete inline error:", err);
                                  setMessage({
                                    type: "error",
                                    text: err.message || "Delete failed",
                                  });
                                }
                              })();
                            }
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---------- Add Modal ---------- */}
      {showAddModal && (
        <div
          className="modal-backdrop"
          onMouseDown={() => setShowAddModal(false)}
        >
          <div
            className="modal-card"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-header">
              <h3>Add Manager</h3>
              <button
                className="close-small"
                onClick={() => setShowAddModal(false)}
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleAddSubmit} className="modal-form">
              <label>Name</label>
              <input
                name="name"
                value={addForm.name}
                onChange={handleAddChange}
                required
              />

              <label>Email</label>
              <input
                name="email"
                type="email"
                value={addForm.email}
                onChange={handleAddChange}
                required
              />

              <label>Position</label>
              <input
                name="position"
                value={addForm.position}
                onChange={handleAddChange}
              />

              <label>Salary</label>
              <input
                name="salary"
                type="number"
                value={addForm.salary}
                onChange={handleAddChange}
              />

              <label>Password</label>
              <input
                name="password"
                type="password"
                value={addForm.password}
                onChange={handleAddChange}
                required
              />

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowAddModal(false)}
                  disabled={adding}
                >
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={adding}>
                  {adding ? "Adding..." : "Add Manager"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------- Edit Modal ---------- */}
      {showEditModal && editingManager && (
        <div
          className="modal-backdrop"
          onMouseDown={() => setShowEditModal(false)}
        >
          <div
            className="modal-card"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-header">
              <h3>Edit Manager</h3>
              <button
                className="close-small"
                onClick={() => setShowEditModal(false)}
              >
                <X size={16} />
              </button>
            </div>

            <div className="modal-form">
              <label>Name</label>
              <input
                name="name"
                value={editingManager.name || ""}
                onChange={handleEditChange}
              />

              <label>Email (readonly)</label>
              <input name="email" value={editingManager.email || ""} readOnly />

              <label>Position</label>
              <input
                name="position"
                value={editingManager.position || ""}
                onChange={handleEditChange}
              />

              <label>Salary</label>
              <input
                name="salary"
                type="number"
                value={editingManager.salary ?? ""}
                onChange={handleEditChange}
              />

              {/* --- Password Change Section --- */}
              <ChangePasswordSection
                API_URL={API_URL}
                email={editingManager.email}
                getAuthHeaders={getAuthHeaders}
                onSuccess={() => {
                  setMessage({ type: "success", text: "Password changed." });
                }}
              />

              <div className="modal-actions">
                <button
                  className="delete-btn"
                  onClick={handleDeleteManager}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
                <div style={{ flex: 1 }} />
                <button
                  className="cancel-btn"
                  onClick={() => setShowEditModal(false)}
                  disabled={savingEdit || deleting}
                >
                  Cancel
                </button>
                <button
                  className="save-btn"
                  onClick={handleSaveEdit}
                  disabled={savingEdit || deleting}
                >
                  {savingEdit ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Change Password Subcomponent ----------------
function ChangePasswordSection({ API_URL, email, getAuthHeaders, onSuccess }) {
  const [show, setShow] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  async function handleChangePassword() {
    // admin reset should use PUT /api/users/update-password/:email
    if (!email) {
      setMsg({ type: "error", text: "Email missing for this user." });
      return;
    }

    if (!password || !confirm) {
      setMsg({ type: "error", text: "Both fields are required." });
      return;
    }
    if (password !== confirm) {
      setMsg({ type: "error", text: "Passwords do not match." });
      return;
    }
    if (password.length < 6) {
      setMsg({ type: "error", text: "Password must be at least 6 characters." });
      return;
    }

    try {
      setSaving(true);
      setMsg(null);

      const url = `${API_URL}/api/users/update-password/${encodeURIComponent(email)}`;
      const res = await fetch(url, {
        method: "PUT",
        headers: getAuthHeaders(true),
        body: JSON.stringify({ password }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error((data && (data.error || data.message)) || `Status ${res.status}`);
      }

      setMsg({ type: "success", text: "Password changed successfully." });
      setPassword("");
      setConfirm("");
      setShow(false);
      if (typeof onSuccess === "function") onSuccess();
    } catch (err) {
      console.error("Change password (admin) error:", err);
      setMsg({ type: "error", text: err.message || "Password change failed" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="change-password-section" style={{ marginTop: 12 }}>
      <button
        type="button"
        className="cancel-btn"
        onClick={() => {
          setShow((s) => !s);
          setMsg(null);
          setPassword("");
          setConfirm("");
        }}
        style={{ marginTop: 10, display: "inline-flex", gap: 8, alignItems: "center" }}
      >
        {show ? "Cancel Password Change" : "Change Password"}
      </button>

      {show && (
        <div className="change-password-fields" style={{ marginTop: 10 }}>
          <label>New Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <label>Confirm Password</label>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />

          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button
              type="button"
              className="save-btn"
              onClick={handleChangePassword}
              disabled={saving}
            >
              {saving ? "Changing..." : "Confirm Change"}
            </button>
          </div>

          {msg && (
            <p
              style={{
                color: msg.type === "error" ? "#b91c1c" : "#065f46",
                marginTop: 8,
                fontSize: "0.95rem",
                fontWeight: 600,
              }}
            >
              {msg.text}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
